import { AsyncPipe } from '@angular/common';
import { Component, inject, output } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faLocationArrow } from '@fortawesome/free-solid-svg-icons';
import { EMPTY, catchError, finalize, throwError } from 'rxjs';

import { CallApiService } from '../../services/api-action.service';
import {
  DeviceLocationError,
  DeviceLocationFailure,
  SearchLocation,
  SearchLocationService,
} from '../../services/search-location.service';

/** PLZ and/or Ort — the backend refuses an empty pair (`geoPlaceRequired`), so the form does too. */
export function buildLocationPickerForm(fb: FormBuilder) {
  return fb.group(
    {
      postalCode: fb.control(''),
      city: fb.control(''),
    },
    { validators: placeRequired },
  );
}

type LocationPickerForm = ReturnType<typeof buildLocationPickerForm>;

function placeRequired(group: AbstractControl): ValidationErrors | null {
  const value = group.value as { postalCode?: string | null; city?: string | null };
  return value?.postalCode?.trim() || value?.city?.trim() ? null : { placeRequired: true };
}

/**
 * #172: the location source chain as the user operates it — „Meinen Standort verwenden" (the
 * device, on this click and never before), or a typed postcode / city (`GET api/geo/place`),
 * plus „Standort entfernen". Mounted twice: in the header chip's dropdown
 * (`app-location-indicator`) and inline on the results page while no location is chosen.
 *
 * Every failure ends in a calm sentence beside the control the user just used and leaves the
 * other entry available: a denied or unavailable device position offers the manual entry, an
 * unknown place says so at the fields. None of them touches the result list, which runs the
 * plain search until a location exists (backend #678 decision 9).
 *
 * Both lookups are reads, so neither needs `[asyncClick]`; a `busy` flag per button keeps a
 * second click from starting a second lookup.
 */
@Component({
  imports: [ReactiveFormsModule, FontAwesomeModule, AsyncPipe],
  selector: 'app-location-picker',
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.css'],
})
export class LocationPickerComponent {
  private fb = inject(FormBuilder);
  private apiActionService = inject(CallApiService);
  readonly locationService = inject(SearchLocationService);

  /** A location was chosen — the header dropdown closes on it; the results page re-searches by itself. */
  readonly picked = output<SearchLocation>();

  readonly faLocationArrow = faLocationArrow;
  readonly form: LocationPickerForm = buildLocationPickerForm(this.fb);

  deviceBusy = false;
  placeBusy = false;
  deviceHint: string | null = null;
  placeHint: string | null = null;

  useDevicePosition(): void {
    if (this.deviceBusy) {
      return;
    }
    this.deviceBusy = true;
    this.deviceHint = null;
    this.locationService
      .useDevicePosition()
      .pipe(finalize(() => (this.deviceBusy = false)))
      .subscribe({
        next: (location) => this.picked.emit(location),
        error: (err: unknown) =>
          (this.deviceHint = deviceFailureText(err instanceof DeviceLocationError ? err.reason : 'unavailable')),
      });
  }

  submitPlace(): void {
    if (this.placeBusy || this.form.invalid) {
      return;
    }
    this.placeBusy = true;
    this.placeHint = null;
    const { postalCode, city } = this.form.getRawValue();

    this.apiActionService
      .callWithToasts$(
        this.locationService.usePlace(postalCode, city).pipe(
          // 404 is the geocoder's „no such place" — not a coded error, so it is worded here,
          // at the fields, rather than as the generic toast.
          catchError((err: { status?: number }) => {
            if (err?.status === 404) {
              this.placeHint = $localize`:@@location.place.notFound:Diesen Ort kennen wir nicht. Bitte prüfen Sie Postleitzahl und Ort.`;
              return EMPTY;
            }
            return throwError(() => err);
          }),
        ),
        null,
        $localize`:@@location.place.error:Der Ort konnte nicht ermittelt werden.`,
        this.form,
      )
      .pipe(finalize(() => (this.placeBusy = false)))
      .subscribe({
        next: (location) => this.picked.emit(location),
        // The toast has been shown; nothing else to do with the error.
        error: () => undefined,
      });
  }

  clear(): void {
    this.locationService.clear();
  }
}

function deviceFailureText(reason: DeviceLocationFailure): string {
  switch (reason) {
    case 'denied':
      return $localize`:@@location.device.denied:Der Zugriff auf Ihren Standort wurde abgelehnt. Geben Sie stattdessen Postleitzahl oder Ort ein.`;
    case 'unsupported':
      return $localize`:@@location.device.unsupported:Dieser Browser stellt keinen Standort bereit. Geben Sie stattdessen Postleitzahl oder Ort ein.`;
    default:
      return $localize`:@@location.device.unavailable:Ihr Standort konnte gerade nicht ermittelt werden. Geben Sie stattdessen Postleitzahl oder Ort ein.`;
  }
}
